import * as React from 'react';
import { Box, Divider, Typography, styled } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import HomeButton from './HomeButton';
import SearchButton from './SearchButton';
import GalleryButton from './GalleryButton';
import SettingButton from './SettingButton';
import MyFamilyTreeButton from './MyFamilyTreeButton';
import FindRelationButton from './FindRelationButton';
import FindPartnerButton from './FindPartnerButton';
import AdminDashboardButton from './AdminDashboardButton';
import AdminAddressButton from './AdminAddressButton';

// Styled components
// FIXED: shouldForwardProp prevents isOpen from leaking to DOM elements
const SidebarSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    marginBottom: 0,
  },
  ...(isOpen === false && {
    marginBottom: 0,
  }),
}));

// FIXED: shouldForwardProp prevents isOpen from leaking to DOM elements
const SectionTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  padding: theme.spacing(0, 2),
  marginBottom: theme.spacing(1),
  transition: 'opacity 0.3s ease',
  opacity: isOpen ? 1 : 0,
  height: isOpen ? 'auto' : 0,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    height: '8px',
    padding: theme.spacing(0, 1),
    visibility: 'hidden',
  }
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0.5, 1),
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0.5, 0.5),
    '& .MuiListItemText-primary': {
      display: 'none',
    },
    '& .MuiListItemIcon-root': {
      minWidth: 'auto',
      marginRight: 0,
    }
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  '&.Mui-selected': {
    backgroundColor: `${theme.palette.primary.light} !important`,
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
}));

const UpgradePlanButton = ({ open }) => {
  const navigate = useNavigate();
  return (
    <StyledListItemButton onClick={() => navigate('/upgrade')}>
      <ListItemIcon sx={{ minWidth: open ? 40 : 'auto' }}>
        <StarIcon sx={{ color: '#f59e0b' }} />
      </ListItemIcon>
      {open !== false && <ListItemText primary="Upgrade Plan" />}
    </StyledListItemButton>
  );
};

// Inside listItems.js
export const MainListItems = ({ open }) => { // Accept the open prop
  const [isAdmin, setIsAdmin] = React.useState(localStorage.getItem('userRole') === '1');

  // Optional: Add effect to listen for role changes
  React.useEffect(() => {
    const checkAdminStatus = () => {
      setIsAdmin(localStorage.getItem('userRole') === '1');
    };  

    // Check initially
    checkAdminStatus();

    // Optional: Listen for storage changes
    window.addEventListener('storage', checkAdminStatus);
    return () => window.removeEventListener('storage', checkAdminStatus);
  }, []);

  return (
    <Box sx={{ 
      pt: 2,
      '& .MuiListItemButton-root': {
        px: { xs: 1, sm: 2 }
      }
    }}>
      {/* Admin Section */}
      {isAdmin && (
        <SidebarSection isOpen={open}>
          <SectionTitle isOpen={open}>Administration</SectionTitle>
          <AdminDashboardButton 
            component={StyledListItemButton} 
            open={open}
          />
          <AdminAddressButton 
            component={StyledListItemButton} 
            open={open}
          />
        </SidebarSection>
      )}

      {/* Main Navigation */}
      <SidebarSection isOpen={open}>
        <SectionTitle isOpen={open}>Main Navigation</SectionTitle>
        <HomeButton component={StyledListItemButton} />
        <MyFamilyTreeButton component={StyledListItemButton} />
      </SidebarSection>

      {/* Search & Discovery */}
      <SidebarSection isOpen={open}>
        <SectionTitle isOpen={open}>Search & Discovery</SectionTitle>
        <SearchButton component={StyledListItemButton} />
        <FindRelationButton component={StyledListItemButton} />
        <FindPartnerButton component={StyledListItemButton} />
      </SidebarSection>

      {/* Media */}
      <SidebarSection isOpen={open}>
        <SectionTitle isOpen={open}>Media</SectionTitle>
        <GalleryButton component={StyledListItemButton} />
      </SidebarSection>

      {/* Settings */}
      <SidebarSection isOpen={open}>
        <SectionTitle isOpen={open}>Settings</SectionTitle>
        <SettingButton component={StyledListItemButton} />
      </SidebarSection>

      {/* Upgrade Plan */}
      {!isAdmin && (
        <SidebarSection isOpen={open}>
          <SectionTitle isOpen={open}>Account</SectionTitle>
          <UpgradePlanButton open={open} />
        </SidebarSection>
      )}
    </Box>
  );
};

export const mainListItems = <MainListItems />;
